import { track } from '@gonfalon/analytics/track';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { snackbarQueue } from '@launchpad-ui/components';

import { load, save } from '#sources/AccountLocalStorage';

export type SdkVersionContextUsage = {
  name: string;
  version: string;
  ldLatestVersion: string;
  latestReleaseUrl: string;
  eolStatus: 'EolPast' | 'EolNear' | 'MajorVersionAvailable' | 'EolAllClear';
};

const notificationSortOrder = {
  EolPast: 0,
  EolNear: 1,
  MajorVersionAvailable: 2,
  EolAllClear: 3,
};

export const trackCtaClick = (usageData: SdkVersionContextUsage) => {
  track('Sdk latest version cta clicked', 'Sdk notifications', usageData);
  return true;
};

export const createCta = (ctaText: string, usageData: SdkVersionContextUsage) => (
  <>
    <a
      onClick={() => trackCtaClick(usageData)}
      href={getDocumentationUrl(
        'sdk/concepts/version-notifications#getting-notifications-in-the-launchdarkly-web-application',
      )}
      target="_blank"
      rel="noreferrer"
    >
      {ctaText}
    </a>
    .
  </>
);

export const saveLS = (projKey: string, envKey: string, sdk: SdkVersionContextUsage) => {
  save(load().setIn(['sdkNotifications', projKey, envKey, sdk.name], sdk));
};

const loadLS = (projKey: string, envKey: string, sdk: SdkVersionContextUsage) =>
  load().get('sdkNotifications').get(projKey)?.get(envKey)?.get(sdk.name)?.toJS() as SdkVersionContextUsage;

export function iterateAndDisplayNotifications(
  sdks: SdkVersionContextUsage[] | undefined,
  projKey: string,
  envKey: string,
) {
  if (!sdks || sdks.length === 0) {
    return;
  }

  sdks.sort((a, b) => notificationSortOrder[a.eolStatus] - notificationSortOrder[b.eolStatus]);
  sdks.forEach((s) => {
    const ls = loadLS(projKey, envKey, s);
    const { name, ldLatestVersion, eolStatus, version } = s;

    if (!ls || !(ls.eolStatus === eolStatus && ls.version === version && ls.ldLatestVersion === ldLatestVersion)) {
      let snackbarProps: Parameters<typeof snackbarQueue.add>[0] | undefined = {
        description: '',
        status: 'info',
      };

      switch (eolStatus) {
        case 'EolPast': {
          snackbarProps.status = 'error';
          snackbarProps.title = `Action required: ${name} v${version} is no longer supported`;
          snackbarProps.description = 'Please update your application to the';
          snackbarProps.action = createCta('latest version', s);
          break;
        }
        case 'EolNear': {
          snackbarProps.status = 'error';
          snackbarProps.title = `Action required: ${name} v${version} is nearing end-of-life`;
          snackbarProps.description = 'Please update your application to the';
          snackbarProps.action = createCta('latest version', s);
          break;
        }
        default: // ignore MajorVersionAvailable && AllClear
          snackbarProps = undefined;
          break;
      }

      if (snackbarProps) {
        if (name === '.NET Client SDK' && version.startsWith('1.')) {
          snackbarProps.description = `The Xamarin SDK has been rebranded as ${name}. ${snackbarProps.description}`;
        }

        snackbarQueue.add(snackbarProps, { onClose: () => saveLS(projKey, envKey, s) });
      }
    }
  });
}
